import {
    Component,
    effect,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { map, Subscription } from 'rxjs';
import { CaliperFactoryService } from '../utils/caliper-factory.service';
import { ChartViewModel } from '../view-models/chart-viewmodel';
import { MeasuresViewModel } from '../view-models/measures-viewmodel';
import { ChannelType } from '../models/ichannel';
import { UniqueNameService } from '../utils/unique-name.service';
import { Zoomer } from '../utils/zoomer';
import { EcgMeasuresService } from '../utils/ecg-measures.service';
import { Layout } from '../layout/layout';
import { ArrangementType, CaliperMode } from '../models/enums';
import { ICalculationContext, ICaliperMessage, ITraceViewModel } from '../models/interfaces';
import { MeasuresSummaryViewModel } from '../view-models/measures-summary-viewmodel';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ecgViewerActions } from '../../store/actions';
import { ECGState } from '../../store/reducers/ecg-viewer.reducer';
import {
    selectorCaliperMode,
    selectorChartState,
    selectorFullState,
    selectorZoomFactor,
} from '../../store/selectors/ecg-viegew.selectors';
import {
    CALIPER_ADD,
    CALIPER_MODIFY,
    CALIPER_REMOVE,
    CaliperMessageService,
} from '../../store/caliper-message.service';
import { AsyncPipe, NgForOf } from '@angular/common';
import { LayersCenteringComponent } from './layers-centering/layers-centering.component';
import { LayerComponent } from './layer/layer.component';

@Component({
    selector: 'ecg-chart',
    templateUrl: './ecg-chart.component.html',
    styleUrls: ['./ecg-chart.component.css'],
    viewProviders: [Layout, Zoomer],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [AsyncPipe, LayersCenteringComponent, NgForOf, LayerComponent],
})
export class EcgChartComponent implements ICalculationContext, OnDestroy {
    @Output()
    caliperActivated = new EventEmitter<boolean>();

    // data members
    @Input()
    title: string = '';

    @Input()
    hideWhileCentering: boolean = true;

    actualWidth: number = 0;
    actualHeight: number = 0;
    translate: any;
    measuresSummary = new MeasuresSummaryViewModel();
    subscriptions: Subscription[] = [];
    arrangement$ = this.ecgStore.select(selectorChartState).pipe(map((s) => s.arrangement));
    private lastCaliperMode = CaliperMode.single;

    //arrangement = this.ecgStore.selectSignal(selectorArrangement)();

    // constructor

    constructor(
        public readonly layout: Layout,
        public element: ElementRef,
        private caliperFactoryService: CaliperFactoryService,
        private ecgMeasuresService: EcgMeasuresService,
        private zoomer: Zoomer,
        private uniqueNameService: UniqueNameService,
        private ecgStore: Store<ECGState>,
        private actions: Actions,
        private caliperMessageService: CaliperMessageService,
    ) {
        this.listenForEvents();

        effect(() => {
            this.layout.ecgState = this.ecgStore.selectSignal(selectorFullState)();
        });

        effect(() => {
            const zoomFactor = this.ecgStore.selectSignal(selectorZoomFactor)();
            // Ensure we always have a valid number (default to 1 if undefined/NaN)
            this.zoomer.factor = (typeof zoomFactor === 'number' && !isNaN(zoomFactor)) ? zoomFactor : 1;
        });

        effect(() => {
            this.lastCaliperMode = this.ecgStore.selectSignal(selectorCaliperMode)();
        });

        this.subscriptions.push(
            this.actions.pipe(ofType(ecgViewerActions.baselineMessage)).subscribe(() => {
                this.chart.layers.forEach((layer) =>
                    layer.traces.forEach((trace) => {
                        let offset = trace.channel?.samples[0].value;

                        // SAMPLES
                        trace.channel?.samples.forEach((sample) => {
                            sample.value = sample.value - (offset || 0);
                        });

                        trace.updateTrace();
                    }),
                );
            }),

            this.actions.pipe(ofType(ecgViewerActions.paperRateMessage)).subscribe(() => {
                this.clearCaliperMeasures();
                this.updateMeasuresSummaryVisibility(true);
            }),

            this.actions.pipe(ofType(ecgViewerActions.arrangementMessage)).subscribe(() => {
                this.clearCaliperMeasures();
                this.updateMeasuresSummaryVisibility(true);
            }),

            this.actions.pipe(ofType(ecgViewerActions.summaryVisibleMessage)).subscribe(() => {
                this.clearCaliperMeasures();
                this.updateMeasuresSummaryVisibility(true);
            }),

            this.actions.pipe(ofType(ecgViewerActions.caliperAddMessage)).subscribe((action) => {
                if (this.lastCaliperMode != this.ecgStore.selectSignal(selectorCaliperMode)()) {
                    this.clearCaliperMeasures();
                    this.lastCaliperMode = this.ecgStore.selectSignal(selectorCaliperMode)();
                }

                this.caliperAdd(action.caliperMessage);
                this.updateMeasuresSummaryVisibility();
            }),

            this.actions.pipe(ofType(ecgViewerActions.caliperModifyMessage)).subscribe((action) => {
                if (this.lastCaliperMode != this.ecgStore.selectSignal(selectorCaliperMode)()) {
                    this.clearCaliperMeasures();
                    this.lastCaliperMode = this.ecgStore.selectSignal(selectorCaliperMode)();
                }

                this.caliperModify(action.caliperMessage);
                this.updateMeasuresSummaryVisibility();
            }),

            this.actions.pipe(ofType(ecgViewerActions.caliperRemoveMessage)).subscribe((action) => {
                if (this.lastCaliperMode != this.ecgStore.selectSignal(selectorCaliperMode)()) {
                    this.clearCaliperMeasures();
                    this.lastCaliperMode = this.ecgStore.selectSignal(selectorCaliperMode)();
                }

                this.caliperRemove(action.caliperMessage);
                this.updateMeasuresSummaryVisibility();
            }),
        );
    }

    // ICalculationContext interface
    get leadWidth(): number {
        return this.layout.context?.leadWidth || 0;
    }

    get leadHeight(): number {
        return this.layout.context?.leadHeight || 0;
    }

    get columns(): number {
        return this.layout.columns;
    }

    get period(): number {
        return this.layout.ecgState.chartState.arrangement == ArrangementType.Medians ? 1 : this.layout.columns;
    }

    // properties
    private _chart!: ChartViewModel;

    get chart() {
        return this._chart;
    }

    @Input()
    set chart(chart: ChartViewModel) {
        this.updateChart(chart);
    }

    ngOnDestroy() {
        this.zoomer.cleanup();
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    // public functions
    updateState() {
        this.layout.ecgState = this.ecgStore.selectSignal(selectorFullState)();
        //this.zoomer.factor = this.store.getState().chartState.zoomFactor;
        const zoomFactor = this.ecgStore.selectSignal(selectorZoomFactor)();
        // Ensure we always have a valid number (default to 1 if undefined/NaN)
        this.zoomer.factor = (typeof zoomFactor === 'number' && !isNaN(zoomFactor)) ? zoomFactor : 1;
    }

    rescale() {
        this.zoomer.rescale();
    }

    fit() {
        this.ecgStore.dispatch(ecgViewerActions.fitMessage());
    }
    clearAllMeasures() {
        this.clearCaliperMeasures();
        this.updateMeasuresSummaryVisibility();
    }

    onMeasuresRemove(measures: MeasuresViewModel) {
        let currentTrace: ITraceViewModel | null = null;
        this.chart.layers.forEach((layer) =>
            layer.traces.forEach((trace) => {
                trace.calipers.forEach((caliper) => {
                    if (caliper == measures.caliper) currentTrace = trace;
                });
            }),
        );

        if (currentTrace) {
            this.updateMeasuresSummaryVisibility();
        }
    }

    enableZoomOnWheel() {
        this.zoomer.enableZoomOnWheel();
    }

    disableZoomOnWheel() {
        this.zoomer.disableZoomOnWheel();
    }

    // private functions
    private updateChart(chart: ChartViewModel) {
        if (chart == null) return;

        this._chart = chart;

        this.layout.init(this.chart);

        this.zoomer.cleanup();
        this.zoomer.init(this.element, this.chart);
    }

    private listenForEvents() {
        this.caliperMessageService.message.subscribe((message) => {
            this.handleCaliperMessages(message.message, message.caliperActionType);
        });

        this.zoomer.zoomFactorChange.subscribe((factor) => {
            this.ecgStore.dispatch(ecgViewerActions.zoomFactorMessage({ zoomFactor: factor }));
        });
        this.zoomer.cyclicFitFired.subscribe(() => {
            this.ecgStore.dispatch(ecgViewerActions.fitMessage());
        });
    }

    private clearCaliperMeasures() {
        this.chart.layers.forEach((layer) =>
            layer.traces.forEach((trace) => {
                trace.calipers.splice(0, trace.calipers.length);
            }),
        );

        this.measuresSummary.measuresHandle.clear();
        this.caliperActivated.emit(false);
    }

    private handleCaliperMessages(message: ICaliperMessage, caliperActionType: string) {
        if (this.lastCaliperMode != this.ecgStore.selectSignal(selectorCaliperMode)()) {
            this.clearCaliperMeasures();
            this.lastCaliperMode = this.ecgStore.selectSignal(selectorCaliperMode)();
        }

        switch (caliperActionType) {
            case CALIPER_ADD:
                this.caliperAdd(message);
                break;
            case CALIPER_REMOVE:
                this.caliperRemove(message);
                break;
            case CALIPER_MODIFY:
                this.caliperModify(message);
                break;
        }
        this.updateMeasuresSummaryVisibility();
    }

    private caliperAdd(message: ICaliperMessage) {
        if (this.ecgStore.selectSignal(selectorCaliperMode)() == CaliperMode.multi) {
            this.clearCaliperMeasures();

            this.chart.layers.forEach((layer) =>
                layer.visibleTraces.forEach((trace) => {
                    let caliper = this.caliperFactoryService.create(trace, { x: message.caliper.getPositionX(), y: 0 });
                    if (caliper) {
                        trace.calipers.push(caliper);
                        caliper.title = this.uniqueNameService.generate(
                            trace.calipers,
                            caliper,
                            ChannelType[trace.channel?.type || ChannelType.UNKNOWN].replace('LEAD_', ''),
                        );
                        this.pushMeasure(new MeasuresViewModel(caliper));
                    }
                }),
            );
        } else {
            message.trace.calipers.push(message.caliper);
            message.caliper.title = this.uniqueNameService.generate(
                message.trace.calipers,
                message.caliper,
                ChannelType[message.trace.channel?.type || ChannelType.UNKNOWN].replace('LEAD_', ''),
            );

            this.pushMeasure(new MeasuresViewModel(message.caliper));
        }
    }

    private pushMeasure(measure: MeasuresViewModel) {
        setTimeout(() => {
            this.measuresSummary.measuresHandle.measuresList.push(measure);
            if (measure.caliper.measures)
                this.measuresSummary.measuresHandle.update(measure.caliper, measure.caliper.measures);
            if (this.measuresSummary.measuresHandle.count === 1) this.caliperActivated.emit(true);
            this.ecgStore.dispatch(ecgViewerActions.measureListChangedMessage());
        });
    }

    private caliperRemove(message: ICaliperMessage) {
        message.trace.calipers.splice(message.trace.calipers.indexOf(message.caliper), 1);
        if (this.ecgStore.selectSignal(selectorCaliperMode)() == CaliperMode.multi) this.clearCaliperMeasures();
        else {
            this.measuresSummary.measuresHandle.remove(message.caliper);
            if (this.measuresSummary.measuresHandle.count === 0) this.caliperActivated.emit(false);
        }
    }

    private caliperModify(message: ICaliperMessage) {
        if (this.ecgStore.selectSignal(selectorCaliperMode)() == CaliperMode.multi) {
            this.chart.layers.forEach((layer) =>
                layer.visibleTraces.forEach((trace) => {
                    if (message.caliper != trace.calipers[0] && trace.calipers.length == 1) {
                        trace.calipers[0].updatePosition(
                            message.caliper.getPositionX(),
                            Math.abs(message.caliper.pointer2.positionX - message.caliper.pointer1.positionX),
                            message.caliper.pointer1.positionX,
                            message.caliper.pointer2.positionX,
                        );
                        let measures = this.ecgMeasuresService.calculate(trace.calipers[0], this.layout);
                        trace.calipers[0].updateMeasures(measures);
                    }

                    if (trace.calipers[0].measures)
                        this.measuresSummary.measuresHandle.update(trace.calipers[0], trace.calipers[0].measures);
                }),
            );
        } else if (message.caliper.measures)
            this.measuresSummary.measuresHandle.update(message.caliper, message.caliper.measures);

        this.measuresSummary.measuresHandle.select(message.caliper);
    }

    private updateMeasuresSummaryVisibility(forceFit: boolean = false) {
        let visible = this.measuresSummary.visible;

        this.measuresSummary.visible = !this.chart.layers.every((layer) =>
            layer.traces.every((trace) => trace.calipers.length == 0),
        );

        if (this.measuresSummary.visible != visible || forceFit) this.fit();
    }
}
